import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  Check,
  Heart,
  ChevronRight,
  CircleUserRound,
  Cloud,
  Eye,
  EyeOff,
  HardDrive,
  ImagePlus,
  LoaderCircle,
  Lock,
  LogIn,
  Menu,
  MessageCircleHeart,
  MessageSquareText,
  Moon,
  Palette,
  RotateCcw,
  Settings2,
  ShieldCheck,
  Sparkle,
  Sparkles,
  Sun,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react";
import type { FormEvent, KeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { User } from "firebase/auth";
import {
  compressScreenshot,
  deleteCloudMemory,
  deleteSharedResearch,
  downloadCloudMemory,
  sendChat,
  sendResearchFeedback,
  uploadCloudMemory,
} from "./lib/api";
import { trackEvent, trackSessionEnded, trackSessionStarted } from "./lib/analytics";
import {
  currentUser,
  deleteFirebaseAccount,
  initializeFirebase,
  leaveAccountForPrivateSession,
  signInOrCreateWithEmail,
  signInWithApple,
  signInWithGoogle,
  watchUser,
} from "./lib/firebase";
import { clearState, loadState, normalizeTheme, saveState } from "./lib/storage";
import {
  AgeDial,
  DEFAULT_SUPPORT_STYLE,
  HUES,
  PearlieAura,
  PearlieAvatar,
  PearlieLogo,
  PearlieUnderline,
  SUPPORT_STYLES,
  TONE_ICONS,
  TONE_PROMPT,
  TONE_REPLY,
  arrivalLine,
  auraHues,
  optionFor,
  startersFor,
  toneSummary,
} from "./pearlie";
import type { Hue } from "./pearlie";
import type {
  AppTheme,
  ChatMessage,
  DisplayMode,
  GravityAd,
  PendingAttachment,
  PersistedState,
  ResponsePreference,
  UserProfile,
} from "./types";

const CONSENT_VERSION = 3;
// The Worker hostname is authorized in Firebase and the web Services ID is
// registered with Apple, so account backup is available on web too.
const ACCOUNT_SIGN_IN_ENABLED = true;
const OPENINGS = [
  "You don’t have to make it sound polished. What’s going on?",
  "Start wherever you want. What’s been on your mind?",
  "What’s been taking up more of your head than it should?",
  "What do you need to talk through today?",
];

function analyticsError(error: unknown): string {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  if (message.includes("offline") || message.includes("connection")) return "no_internet";
  if (message.includes("too long")) return "timeout";
  if (message.includes("session") || message.includes("sign in")) return "unauthorized";
  if (message.includes("too many") || message.includes("moving quickly")) return "rate_limited";
  if (message.includes("large")) return "attachment_too_large";
  return "backend";
}

const topics = [
  "Relationships", "Friendships", "Family", "Work or studies",
  "Confidence", "Something else", "Just exploring",
];
/** Answers that name no topic, so nothing is saved for them. */
const OPEN_TOPICS = ["Something else", "Just exploring"];

function App() {
  const [state, setReactState] = useState<PersistedState>(() => loadState());
  const stateRef = useRef(state);
  const setState = useCallback<React.Dispatch<React.SetStateAction<PersistedState>>>(action => {
    setReactState(current => {
      const next = typeof action === "function"
        ? (action as (value: PersistedState) => PersistedState)(current)
        : action;
      stateRef.current = next;
      // Persist in the same event as the state change. Waiting for a later
      // effect allowed a tab/app close immediately after Send to lose a turn.
      saveState(next);
      return next;
    });
  }, []);
  const [firebaseStatus, setFirebaseStatus] = useState<"loading" | "ready" | "error">("loading");
  const [firebaseError, setFirebaseError] = useState("");
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const restoredUID = useRef<string | null>(null);

  useEffect(() => {
    stateRef.current = state;
    saveState(state);
  }, [state]);

  useEffect(() => {
    const flush = () => saveState(stateRef.current);
    addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flush);
    return () => {
      removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flush);
    };
  }, []);

  useEffect(() => {
    const enabled = state.profile?.analyticsEnabled ?? false;
    if (!enabled) return;
    trackSessionStarted(true);
    const end = () => trackSessionEnded(true);
    addEventListener("pagehide", end, { once: true });
    return () => removeEventListener("pagehide", end);
  }, [state.profile?.analyticsEnabled]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = state.theme;
    root.dataset.mode = state.displayMode;
    if (state.displayMode === "system") {
      root.dataset.systemDark = matchMedia("(prefers-color-scheme: dark)").matches ? "true" : "false";
    }
  }, [state.theme, state.displayMode]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    initializeFirebase()
      .then(async () => {
        setFirebaseStatus("ready");
        setAuthUser(await currentUser());
        unsubscribe = await watchUser(setAuthUser);
      })
      .catch(error => {
        setFirebaseError(error instanceof Error ? error.message : "Pearlie web access is unavailable.");
        setFirebaseStatus("error");
      });
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    if (!authUser || authUser.isAnonymous || restoredUID.current === authUser.uid) return;
    restoredUID.current = authUser.uid;
    downloadCloudMemory()
      .then(remote => {
        if (!remote) return;
        setState(local => {
          const merged = new Map(local.messages.map(message => [message.id, message]));
          remote.messages.forEach(message => merged.set(message.id, message));
          const messages = [...merged.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          const remoteProfile = remote.profile;
          return {
            ...local,
            introSeen: Boolean(remoteProfile) || local.introSeen,
            profile: remoteProfile ?? local.profile,
            messages,
            notebook: remote.notebook ?? local.notebook,
            lastMessageAt: remote.lastMessageAt ?? local.lastMessageAt,
            theme: remoteProfile ? normalizeTheme(remoteProfile.selectedTheme) : local.theme,
          };
        });
      })
      .catch(() => undefined);
  }, [authUser]);

  useEffect(() => {
    if (!authUser || authUser.isAnonymous || !state.profile) return;
    const timer = window.setTimeout(() => uploadCloudMemory(state).catch(() => undefined), 900);
    return () => clearTimeout(timer);
  }, [authUser, state.profile, state.messages, state.notebook, state.lastMessageAt]);

  const update = (recipe: (current: PersistedState) => PersistedState) => setState(recipe);

  if (firebaseStatus === "loading") return <LaunchLoader />;
  if (firebaseStatus === "error") return <SetupUnavailable message={firebaseError} />;

  return (
    <main className="app-root">
      {!state.introSeen ? (
        <Welcome
          onStart={() => update(current => ({ ...current, introSeen: true }))}
          onRestore={() => setAuthOpen(true)}
        />
      ) : !state.profile ? (
        <Onboarding
          theme={state.theme}
          onComplete={profile => {
            trackEvent(profile.analyticsEnabled, "onboarding_completed");
            update(current => ({
              ...current,
              profile,
              theme: profile.selectedTheme,
              researchSharingEnabled: false,
              researchChoiceMade: false,
            }));
          }}
        />
      ) : (
        <Chat
          state={state}
          user={authUser}
          setState={setState}
          onOpenAccount={() => setAuthOpen(true)}
          onReset={() => setState(clearState())}
        />
      )}

      {ACCOUNT_SIGN_IN_ENABLED && authOpen && (
        <AuthenticationSheet
          user={authUser}
          onClose={() => setAuthOpen(false)}
          onSignedIn={user => {
            setAuthUser(user);
            setAuthOpen(false);
          }}
        />
      )}
    </main>
  );
}

function LaunchLoader() {
  return (
    <div className="full-stage launch-loader">
      <img src="/app/AppIcon-180.png" alt="" className="loader-mark" />
      <div className="brand-word">PEARLIE</div>
    </div>
  );
}

function SetupUnavailable({ message }: { message: string }) {
  return (
    <div className="full-stage setup-unavailable">
      <div className="setup-card">
        <span className="eyebrow">PEARLIE WEB</span>
        <h1>Almost ready.</h1>
        <p>{message}</p>
        <button className="primary dark" onClick={() => location.reload()}>Try again</button>
      </div>
    </div>
  );
}

const WORRIES = ["he left me on read", "my friend went quiet", "should I ask for a raise?"];

function useViewport() {
  const [size, setSize] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  useEffect(() => {
    const resize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    addEventListener("resize", resize);
    return () => removeEventListener("resize", resize);
  }, []);
  return size;
}

function reducedMotion() {
  return matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/// The first screen is a small piece of motion. Pearlie's rose blooms in over a
/// soft glow, and a few of the things women bring her float up around it.
/// Tapping it makes the glow bloom. Same scene as the iPhone app.
function Welcome({ onStart, onRestore }: { onStart: () => void; onRestore: () => void }) {
  const [leaving, setLeaving] = useState(false);
  const [signed, setSigned] = useState(0);
  const [bubbles, setBubbles] = useState(0);
  const [taps, setTaps] = useState(0);
  const viewport = useViewport();
  const pearl = Math.round(Math.min(230, viewport.width * 0.56, viewport.height * 0.27));

  useEffect(() => {
    if (reducedMotion()) { setSigned(1); setBubbles(WORRIES.length); return; }
    const timers = [
      window.setTimeout(() => setSigned(1), 550),
      ...WORRIES.map((_, index) => window.setTimeout(() => setBubbles(index + 1), 1600 + index * 340)),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const start = () => {
    if (leaving) return;
    setLeaving(true);
    window.setTimeout(onStart, 380);
  };

  return (
    <div className={`welcome-stage full-stage ${leaving ? "leaving" : ""}`}>
      <div className="welcome-backdrop" aria-hidden="true" />
      <div className="welcome-content">
        <div className="welcome-topline" aria-label="Pearlie, for women"><span>PEARLIE</span><i /><span className="for-women">FOR WOMEN</span></div>
        <div className="welcome-hero" style={{ ["--pearl" as string]: `${pearl}px` }}>
          <button className="pearl-button" onClick={() => setTaps(value => value + 1)} aria-hidden="true" tabIndex={-1}>
            <PearlieAura diameter={pearl} pulse={taps}>
              <PearlieLogo size={pearl} reveal={signed} duration={1500} />
            </PearlieAura>
          </button>
          {WORRIES.map((worry, index) => (
            <span key={worry} className={`worry worry-${index} ${bubbles > index ? "shown" : ""}`} aria-hidden="true">{worry}</span>
          ))}
        </div>
        <div className="welcome-copy">
          <h1>Say the thing you<br />can’t say <em>out loud.</em></h1>
          <p className="welcome-subtitle">Your AI big sister in your pocket. Honest, kind, and here any time.</p>
          <div className="welcome-trust">
            <span><Lock size={12} strokeWidth={2.6} /> Private</span>
            <span><Heart size={12} strokeWidth={2.6} fill="currentColor" /> Free</span>
            <span><Sparkles size={12} strokeWidth={2.4} /> No sign-up</span>
          </div>
          <div className="welcome-actions">
            <button className="primary" onClick={start} disabled={leaving}>Meet Pearlie <ArrowRight size={17} /></button>
            {ACCOUNT_SIGN_IN_ENABLED && <button className="text-button" onClick={onRestore}>I already have an account</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

type OnboardingDraft = {
  name: string;
  age: string;
  topic: string | null;
  tone: string;
  analyticsEnabled: boolean;
};

const STEP_HUES = ["peach", "butter", "rose", "lilac"] as const;
const STEP_COUNT = STEP_HUES.length;

/// Four quick questions, asked the way Pearlie would ask them. Her pearl sits
/// above every question and answers back: it blooms while you type, takes on
/// the colour of what you pick, and she says one short line in reply. Only the
/// age is required: Pearlie is for 13 and up, and her under-18 safety rules
/// depend on it.
function Onboarding({ theme, onComplete }: { theme: AppTheme; onComplete: (profile: UserProfile) => void }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [arrival, setArrival] = useState(false);
  const [pulse, setPulse] = useState(0);
  const [reaction, setReaction] = useState<string | null>(null);
  const [draft, setDraft] = useState<OnboardingDraft>({
    name: "", age: "", topic: null, tone: DEFAULT_SUPPORT_STYLE, analyticsEnabled: true,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const hues = auraHues(draft.topic, draft.tone);
  const age = draft.age ? Number(draft.age) : null;
  const firstName = draft.name.trim().split(/\s+/)[0] ?? "";

  useEffect(() => {
    setReaction(null);
    if (step === 0) window.setTimeout(() => inputRef.current?.focus(), 430);
  }, [step]);

  const canContinue = [
    true,
    age !== null && age >= 13 && age <= 100,
    draft.topic !== null,
    draft.tone.length > 0,
  ][step];

  const picked = (value: string, wasOn: boolean) => {
    if (!wasOn) { setReaction(optionFor(value).reaction); setPulse(count => count + 1); }
  };

  const selectTopic = (value: string) => {
    picked(value, draft.topic === value);
    setDraft(current => ({ ...current, topic: value }));
  };

  const finish = useCallback(() => {
    const now = new Date().toISOString();
    // Only what she chose is saved. Nothing is inferred from her answers.
    onComplete({
      id: crypto.randomUUID(),
      name: draft.name.trim().slice(0, 100),
      age: Number(draft.age),
      currentSituations: [],
      lifeStory: "",
      goals: draft.topic && !OPEN_TOPICS.includes(draft.topic) ? [draft.topic] : [],
      pressures: [],
      preferredTone: draft.tone,
      alwaysRemember: "",
      dependents: [],
      whyNow: "",
      selectedTheme: theme,
      analyticsEnabled: draft.analyticsEnabled,
      createdAt: now,
      updatedAt: now,
    });
  }, [draft, onComplete, theme]);

  if (arrival) return <Arrival name={draft.name} style={draft.tone} hues={hues} onComplete={finish} />;

  const lastStep = step === STEP_COUNT - 1;
  const next = () => {
    if (!canContinue) return;
    if (lastStep) { setArrival(true); return; }
    setDirection("forward");
    setStep(value => value + 1);
  };

  const asked = (eyebrow: string, extra?: ReactNode) => (
    <div className="asked-by-pearlie">
      <PearlieLogo size={36} pulse={pulse} className="asking-pearl" />
      <div><b>PEARLIE</b><i>{eyebrow}</i></div>
      {extra}
    </div>
  );

  const says = (text: string) => (
    <div key={text} className="pearlie-says" role="status"><PearlieAvatar size={24} /><span>{text}</span></div>
  );

  const chips = (options: string[], selected: string[], toggle: (value: string) => void) => (
    <div className="choice-grid">
      {options.map(option => {
        const { icon: Icon, hue } = optionFor(option);
        const on = selected.includes(option);
        return (
          <button key={option} className={on ? "selected" : ""} style={{ ["--hue" as string]: HUES[hue] }} onClick={() => toggle(option)} aria-pressed={on}>
            <Icon size={15} strokeWidth={2.3} />{option}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="onboarding-stage full-stage" style={{ ["--step-hue" as string]: HUES[STEP_HUES[step]] }}>
      <section className="onboarding-shell">
        <header className="onboarding-header">
          <button
            className={`circle-button ${step === 0 ? "invisible" : ""}`}
            onClick={() => { setDirection("back"); setStep(value => Math.max(0, value - 1)); }}
            aria-label="Previous question"
          ><ArrowLeft size={18} /></button>
          <span>{step + 1} of {STEP_COUNT}</span>
        </header>
        <div className="progress-track"><i style={{ width: `${((step + 1) / STEP_COUNT) * 100}%` }} /></div>

        <div key={step} className={`onboarding-content enter-${direction}`}>
          {step === 0 && (
            <section className="question">
              {asked("Let's start simple.")}
              <h1>What should I<br />call you?</h1>
              <p className="question-hint">A nickname is fine. Or skip it.</p>
              <div className="name-field">
                <input
                  ref={inputRef}
                  className="hero-input name-input"
                  value={draft.name}
                  onChange={event => { setDraft(current => ({ ...current, name: event.target.value })); setPulse(count => count + 1); }}
                  onKeyDown={event => event.key === "Enter" && next()}
                  placeholder="Your name"
                  autoComplete="given-name"
                  maxLength={100}
                  aria-label="Your name"
                />
                <PearlieUnderline progress={draft.name.trim() ? Math.min(1, 0.18 + draft.name.trim().length / 9) : 0} />
              </div>
              {firstName.length >= 2 && says(`Nice to meet you, ${firstName}.`)}
            </section>
          )}
          {step === 1 && (
            <section className="question">
              {asked("No judgment. Just context.")}
              <h1>How old<br />are you?</h1>
              <p className="question-hint">Pearlie is for ages 13 and up. Your age shapes her advice, with extra care for anyone under 18.</p>
              <div className={`age-readout ${age === null ? "unset" : ""}`} aria-hidden="true"><b>{age ?? 25}</b><span>years old</span></div>
              <AgeDial value={age} start={25} onChange={value => { setDraft(current => ({ ...current, age: String(value) })); setPulse(count => count + 1); }} />
              {age === null && <small className="question-small">Slide to your age.</small>}
              {age !== null && says("Thanks. That helps me give you better advice.")}
            </section>
          )}
          {step === 2 && (
            <section className="question">
              {asked("Pick one. You can change it later.")}
              <h1>What’s on your<br />mind today?</h1>
              {chips(topics, draft.topic ? [draft.topic] : [], selectTopic)}
              {reaction && draft.topic && says(reaction)}
            </section>
          )}
          {step === 3 && (
            <section className="question">
              {asked("You can change this any time.")}
              <h1>How should I<br />support you?</h1>
              <div className="tone-preview" aria-label={`A real reply. You: ${TONE_PROMPT}. Pearlie: ${TONE_REPLY}. ${toneSummary(draft.tone)}`}>
                <div className="tone-preview-user">{TONE_PROMPT}</div>
                <div className="tone-preview-pearlie"><PearlieAvatar size={26} /><p>{TONE_REPLY}</p></div>
                <div className="tone-summary"><Sparkle size={11} strokeWidth={2.6} /><span key={draft.tone}>{toneSummary(draft.tone)}</span></div>
              </div>
              <div className="tone-grid">
                {SUPPORT_STYLES.map(tone => {
                  const Icon = TONE_ICONS[tone] ?? Sparkles;
                  return (
                    <button key={tone} className={`tone-card ${draft.tone === tone ? "selected" : ""}`} onClick={() => { setDraft(current => ({ ...current, tone })); setPulse(count => count + 1); }} aria-pressed={draft.tone === tone}>
                      <Icon size={16} strokeWidth={2.4} /><span>{tone}</span>
                    </button>
                  );
                })}
              </div>
              <label className="analytics-choice">
                <span className="analytics-icon"><Sparkles size={16} /></span>
                <span><b>App analytics</b><small>Usage stats, like which screens you open and how often you chat, sent only to Pearlie’s own server. Never your messages or answers. Change it any time in Settings.</small></span>
                <input type="checkbox" role="switch" checked={draft.analyticsEnabled} onChange={event => setDraft(current => ({ ...current, analyticsEnabled: event.target.checked }))} />
              </label>
            </section>
          )}
        </div>

        <button className="primary accent" disabled={!canContinue} onClick={next}>
          {lastStep ? "Meet Pearlie" : step === 0 && !draft.name.trim() ? "Skip" : "Continue"}
        </button>
      </section>
    </div>
  );
}

const ARRIVAL_LINES = [
  "Learning what matters to you",
  "Setting up your space",
  "Getting ready to listen",
];

/// The handoff from questions to the relationship. Her pearl gathers the
/// colours of everything you picked while three lines tick by, then her rose
/// blooms in over it and she says hello. Click to skip.
function Arrival({ name, style, hues, onComplete }: { name: string; style: string; hues: Hue[]; onComplete: () => void }) {
  const [stage, setStage] = useState(0);
  const [shown, setShown] = useState<Hue[]>(["peach", "rose"]);
  const [lines, setLines] = useState(0);
  const [linesLeaving, setLinesLeaving] = useState(false);
  const [signed, setSigned] = useState(0);
  const [energy, setEnergy] = useState(1);
  const finished = useRef(false);
  const firstName = name.trim().split(/\s+/)[0];

  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    setStage(4);
    window.setTimeout(onComplete, reducedMotion() ? 200 : 420);
  }, [onComplete]);

  useEffect(() => {
    if (reducedMotion()) {
      setShown(hues); setSigned(1); setStage(3);
      const timer = window.setTimeout(finish, 2200);
      return () => clearTimeout(timer);
    }
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));
    at(20, () => { setStage(1); setEnergy(2.4); });
    at(450, () => setStage(2));
    hues.filter(hue => hue !== "peach" && hue !== "rose").forEach((hue, index) => at(500 + index * 230, () => setShown(current => current.includes(hue) ? current : [...current, hue])));
    at(600, () => setLines(1));
    at(1160, () => setLines(2));
    at(1720, () => setLines(3));
    at(2250, () => setLinesLeaving(true));
    at(2570, () => { setEnergy(1); setStage(3); setSigned(1); });
    at(5100, finish);
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <button className={`arrival-stage full-stage stage-${stage}`} onClick={() => stage >= 2 && finish()} aria-label={`${firstName ? `${firstName}, ` : ""}Pearlie is here. ${arrivalLine(style)}`}>
      <div className="arrival-pearl">
        <PearlieAura hues={shown} diameter={190} energy={energy} pulse={lines + stage}>
          <PearlieLogo size={190} reveal={signed} duration={1300} />
        </PearlieAura>
      </div>
      <div className="arrival-copy">
        <ul className={`arrival-lines ${stage === 2 && !linesLeaving ? "visible" : ""}`}>
          {ARRIVAL_LINES.map((line, index) => (
            <li key={line} className={lines > index ? "done" : ""}><i><Check size={13} strokeWidth={3} /></i>{line}</li>
          ))}
        </ul>
        <div className={`arrival-title ${stage >= 3 ? "visible" : ""}`}>
          <span className="eyebrow">YOUR CORNER JUST GOT BIGGER</span>
          <h1>{firstName ? <>{firstName},<br /></> : null}Pearlie is here.</h1>
          <p>{arrivalLine(style)}</p>
        </div>
      </div>
      <small className={stage >= 3 ? "visible" : ""}><i /> Tap to continue</small>
    </button>
  );
}

function Chat({
  state,
  user,
  setState,
  onOpenAccount,
  onReset,
}: {
  state: PersistedState;
  user: User | null;
  setState: React.Dispatch<React.SetStateAction<PersistedState>>;
  onOpenAccount: () => void;
  onReset: () => void;
}) {
  const [draft, setDraft] = useState(state.draft);
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null);
  const [preference, setPreference] = useState<ResponsePreference>("automatic");
  const [isReplying, setIsReplying] = useState(false);
  const [error, setError] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [consentPending, setConsentPending] = useState(false);
  const [retry, setRetry] = useState<{ text: string; attachment: PendingAttachment | null; id: string; preference: ResponsePreference } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [researchPromptOpen, setResearchPromptOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const openingRequested = useRef(false);
  // The opener Pearlie just spoke, so her next message can be measured
  // against it: did the line bring her back into the conversation?
  const spokenOpener = useRef<{ id: string; at: number } | null>(null);
  const pendingAfterConsent = useRef<(() => void) | null>(null);
  const profile = state.profile!;

  useEffect(() => {
    const timer = window.setTimeout(() => setState(current => ({ ...current, draft })), 250);
    return () => clearTimeout(timer);
  }, [draft, setState]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [state.messages, isReplying, attachment]);

  useEffect(() => {
    const userMessages = state.messages.filter(message => message.role === "user").length;
    if (isReplying || !user?.isAnonymous || userMessages < 3 || state.signInNudgeShown) return;
    setState(current => current.signInNudgeShown
      ? current
      : { ...current, signInNudgeShown: true });
    setSavePromptOpen(true);
    trackEvent(profile.analyticsEnabled, "sign_in_prompt_shown");
  }, [isReplying, setState, state.messages, state.signInNudgeShown, user]);

  useEffect(() => {
    if (
      state.successfulReplyCount < 5 ||
      state.researchChoiceMade ||
      state.researchSharingEnabled ||
      isReplying ||
      researchPromptOpen ||
      settingsOpen ||
      savePromptOpen ||
      consentPending
    ) return;
    setResearchPromptOpen(true);
    trackEvent(profile.analyticsEnabled, "research_prompt_shown");
  }, [
    consentPending,
    isReplying,
    profile.analyticsEnabled,
    researchPromptOpen,
    savePromptOpen,
    settingsOpen,
    state.researchChoiceMade,
    state.researchSharingEnabled,
    state.successfulReplyCount,
  ]);

  // Return opener. After at least eight hours away, Pearlie may say one line
  // from the notebook before she types — "How'd Thursday go?" — or stay
  // silent, which is the normal outcome. Dropped if she speaks first.
  useEffect(() => {
    if (!state.messages.length || openingRequested.current) return;
    const last = state.messages[state.messages.length - 1];
    const userTurns = state.messages.filter(message => message.role === "user").length;
    const since = Date.parse(state.lastMessageAt ?? last.createdAt);
    const hoursAway = (Date.now() - since) / 3_600_000;
    if (userTurns < 2 || !(hoursAway >= 8) || state.returnOpeningKey === last.id) return;
    openingRequested.current = true;
    setState(current => ({ ...current, returnOpeningKey: last.id }));
    sendChat({
      message: "",
      profile,
      messages: state.messages.slice(-12),
      notebook: state.notebook,
      lastMessageAt: state.lastMessageAt,
      preference: "automatic",
      opening: true,
      gravitySessionID: state.gravitySessionID,
    }).then(response => {
      if (response.silent || !response.reply.trim()) {
        trackEvent(profile.analyticsEnabled, "return_opening_offered", { result: "silent" });
        return;
      }
      const openerID = crypto.randomUUID();
      setState(current => {
        if (current.messages[current.messages.length - 1]?.id !== last.id) return current;
        spokenOpener.current = { id: openerID, at: Date.now() };
        trackEvent(profile.analyticsEnabled, "return_opening_offered", { result: "spoken" });
        return {
          ...current,
          messages: [...current.messages, {
            id: openerID, role: "pearlie", content: response.reply,
            createdAt: new Date().toISOString(),
          }],
          notebook: response.notebook ?? current.notebook,
        };
      });
    }).catch(() => trackEvent(profile.analyticsEnabled, "return_opening_offered", { result: "failed" }));
  }, []);

  useEffect(() => {
    if (state.messages.length || openingRequested.current) return;
    openingRequested.current = true;
    setIsReplying(true);
    sendChat({
      message: "",
      profile,
      messages: [],
      notebook: state.notebook,
      lastMessageAt: state.lastMessageAt,
      preference: "automatic",
      opening: true,
      gravitySessionID: state.gravitySessionID,
    }).then(response => {
      appendPearlieReply(response.reply);
    }).catch(() => {
      const index = [...profile.id].reduce((total, character) => total + character.charCodeAt(0), 0) % OPENINGS.length;
      appendPearlieReply(OPENINGS[index]);
    }).finally(() => setIsReplying(false));
  }, []);

  const appendPearlieReply = (content: string, ad?: GravityAd) => {
    setState(current => ({
      ...current,
      messages: [...current.messages, {
        id: crypto.randomUUID(), role: "pearlie", content,
        createdAt: new Date().toISOString(), ...(ad ? { sponsoredAd: ad } : {}),
      }],
    }));
  };

  const deliver = async (text: string, sentAttachment: PendingAttachment | null, id: string, selectedPreference: ResponsePreference, addBubble: boolean) => {
    const startedAt = performance.now();
    const history = state.messages;
    const visibleText = text.trim() || "What’s happening in this screenshot?";
    const userMessageCount = history.filter(message => message.role === "user").length + (addBubble ? 1 : 0);
    // Ask on every text turn. The Worker's GRAVITY_ENABLED flag and its
    // eligibility rules decide whether a suggestion actually comes back.
    const adRequested = !sentAttachment;
    if (addBubble) {
      trackEvent(profile.analyticsEnabled, "message_sent", {
        message_number: userMessageCount,
        had_attachment: Boolean(sentAttachment),
        response_preference: selectedPreference,
        history_bucket: history.length < 6 ? "0-5" : history.length < 21 ? "6-20" : "21+",
      });
      const previous = history.at(-1);
      if (spokenOpener.current && previous?.id === spokenOpener.current.id) {
        const delaySeconds = Math.max(0, Math.round((Date.now() - spokenOpener.current.at) / 1_000));
        trackEvent(profile.analyticsEnabled, "return_opening_replied", {
          delay_bucket: delaySeconds < 60 ? "under_1m" : delaySeconds < 300 ? "1_5m" : delaySeconds < 3_600 ? "5_60m" : delaySeconds < 86_400 ? "1_24h" : "over_24h",
        });
      }
      spokenOpener.current = null;
      if (previous?.role === "pearlie" && history.some(message => message.role === "user")) {
        const delaySeconds = Math.max(0, Math.round((Date.now() - Date.parse(previous.createdAt)) / 1_000));
        trackEvent(profile.analyticsEnabled, "continued_after_reply", {
          delay_bucket: delaySeconds < 60 ? "under_1m" : delaySeconds < 300 ? "1_5m" : delaySeconds < 3_600 ? "5_60m" : delaySeconds < 86_400 ? "1_24h" : "over_24h",
        });
      }
      setState(current => ({
        ...current,
        draft: "",
        messages: [...current.messages, {
          id, role: "user", content: visibleText, hadAttachment: Boolean(sentAttachment),
          createdAt: new Date().toISOString(),
        }],
      }));
    }
    setDraft("");
    setAttachment(null);
    setPreference("automatic");
    setError("");
    setIsReplying(true);
    if (adRequested) trackEvent(profile.analyticsEnabled, "gravity_ad_requested", { production: true });
    try {
      const response = await sendChat({
        message: text,
        requestID: id,
        profile,
        messages: history,
        notebook: state.notebook,
        lastMessageAt: state.lastMessageAt,
        ...(sentAttachment ? { attachment: sentAttachment } : {}),
        preference: selectedPreference,
        requestSponsoredSuggestion: adRequested,
        gravitySessionID: state.gravitySessionID,
        research: {
          enabled: state.researchSharingEnabled,
          conversationID: state.researchConversationID,
          deletionKey: state.researchDeletionKey,
        },
      });
      setState(current => {
        const messages = current.messages.map(message => message.id === id && response.visualContext
          ? { ...message, visualContext: response.visualContext }
          : message);
        return {
          ...current,
          messages: [...messages, {
            id: crypto.randomUUID(), role: "pearlie", content: response.reply,
            createdAt: new Date().toISOString(),
          }],
          currentAd: response.safetySignal ? undefined : (response.sponsoredAd ?? current.currentAd),
          notebook: response.notebook ?? current.notebook,
          lastMessageAt: new Date().toISOString(),
          successfulReplyCount: current.successfulReplyCount + 1,
        };
      });
      setRetry(null);
      trackEvent(profile.analyticsEnabled, "successful_reply", {
        reply_number: userMessageCount,
        had_attachment: Boolean(sentAttachment),
      });
      trackEvent(profile.analyticsEnabled, "chat_request_succeeded", {
        latency_ms: Math.round(performance.now() - startedAt),
        had_attachment: Boolean(sentAttachment),
        ad_requested: adRequested,
        ad_received: Boolean(response.sponsoredAd),
        safety_signal: Boolean(response.safetySignal),
        reply_length_bucket: response.reply.length < 160 ? "short" : response.reply.length < 500 ? "medium" : "long",
      });
      if (response.sponsoredAd) trackEvent(profile.analyticsEnabled, "gravity_ad_received", { production: true });
      else if (adRequested) trackEvent(profile.analyticsEnabled, "gravity_ad_no_fill", { result: "empty", production: true });
    } catch (sendError) {
      trackEvent(profile.analyticsEnabled, "chat_request_failed", {
        error: analyticsError(sendError),
        latency_ms: Math.round(performance.now() - startedAt),
        had_attachment: Boolean(sentAttachment),
      });
      setError(sendError instanceof Error ? sendError.message : "Pearlie is briefly unavailable.");
      setRetry({ text, attachment: sentAttachment, id, preference: selectedPreference });
    } finally {
      setIsReplying(false);
    }
  };

  const requestSend = () => {
    const text = draft.trim();
    if ((!text && !attachment) || isReplying || uploadingImage) return;
    const action = () => deliver(text, attachment, crypto.randomUUID(), preference, true);
    if (state.consentVersion < CONSENT_VERSION) {
      pendingAfterConsent.current = action;
      setConsentPending(true);
      return;
    }
    action();
  };

  const handleKeyboard = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      requestSend();
    }
  };

  const handleImage = async (file?: File) => {
    if (!file) return;
    setUploadingImage(true);
    setError("");
    try {
      setAttachment(await compressScreenshot(file));
      trackEvent(profile.analyticsEnabled, "screenshot_action", { action: "attached", result: "succeeded" });
    }
    catch (imageError) {
      trackEvent(profile.analyticsEnabled, "screenshot_action", { action: "attach", result: "failed" });
      setError(imageError instanceof Error ? imageError.message : "Pearlie couldn’t read that image.");
    }
    finally { setUploadingImage(false); }
  };

  const firstName = profile.name.split(/\s+/)[0];
  const presence = attachment ? `Let me look at this properly, ${firstName}.` : `Alright, ${firstName}.`;
  const conversationStorage = user?.isAnonymous
    ? { icon: <HardDrive size={16} />, title: "Saved in this browser", detail: "Sign in for backup across devices" }
    : { icon: <Cloud size={16} />, title: "Private backup active", detail: "Available on your other devices" };

  return (
    <div className="chat-stage">
      <aside className="desktop-sidebar">
        <div className="sidebar-brand">
          <img src="/app/AppIcon-60.png" alt="" />
          <div><b>Pearlie</b><span>AI advice for women</span></div>
        </div>

        <nav className="sidebar-navigation" aria-label="Pearlie navigation">
          <button className="selected"><MessageSquareText size={18} /><span>Conversation</span></button>
          <button onClick={() => {
            trackEvent(profile.analyticsEnabled, "settings_opened");
            setSettingsOpen(true);
          }}><Settings2 size={18} /><span>Settings</span></button>
        </nav>

        <div className="sidebar-bottom">
          <div className="sidebar-storage">
            <span className="sidebar-storage-icon">{conversationStorage.icon}</span>
            <div><b>{conversationStorage.title}</b><small>{conversationStorage.detail}</small></div>
          </div>
          {ACCOUNT_SIGN_IN_ENABLED && (
            <button className="sidebar-account" onClick={onOpenAccount}>
              <span>{firstName.charAt(0).toUpperCase()}</span>
              <div><b>{firstName}</b><small>{user?.isAnonymous ? "Back up conversations" : user?.email || "Account connected"}</small></div>
              <ChevronRight size={16} />
            </button>
          )}
          <div className="sidebar-links"><a href="/privacy" target="_blank">Privacy</a><a href="/support" target="_blank">Support</a></div>
        </div>
      </aside>

      <section className="chat-workspace">
        <header className="desktop-chat-header">
          <div>
            <span className="eyebrow">YOUR PRIVATE SPACE</span>
            <h1>Conversation</h1>
          </div>
          <div className="workspace-status">
            <i />
            <div><b>Pearlie is here</b><small>{user?.isAnonymous ? "Saved locally" : "Private backup active"}</small></div>
          </div>
        </header>

        <header className="chat-header mobile-chat-header">
          <div className="header-spacer" />
          <div className="chat-brand"><PearlieAvatar size={31} /><span>Pearlie</span></div>
          <button className="circle-button" onClick={() => {
            trackEvent(profile.analyticsEnabled, "settings_opened");
            setSettingsOpen(true);
          }} aria-label="Open settings"><Menu size={20} /></button>
        </header>

        <div className="message-scroll" ref={listRef}>
          <div className="message-column">
            {state.messages.map((message, index) => (
              <Message key={message.id} message={message} isLatest={index === state.messages.length - 1 && state.messages.slice(0, index).some(item => item.role === "user")} onFeedback={feedback => {
                setState(current => ({ ...current, messages: current.messages.map(item => item.id === message.id ? { ...item, feedback } : item) }));
                trackEvent(profile.analyticsEnabled, "response_feedback", { feedback });
                const requestID = [...state.messages.slice(0, index)].reverse().find(item => item.role === "user")?.id;
                if (state.researchSharingEnabled && requestID) {
                  void sendResearchFeedback(requestID, feedback).catch(() => undefined);
                }
              }} />
            ))}
            {state.messages.length === 1 && state.messages[0].role === "pearlie" && !isReplying && (
              <Starters lines={startersFor(profile)} onPick={line => {
                setDraft(line);
                window.setTimeout(() => inputRef.current?.focus(), 0);
                trackEvent(profile.analyticsEnabled, "starter_tapped");
              }} />
            )}
            {isReplying && <Presence text={presence} />}
            {error && <div className="chat-error"><span>{error}</span>{retry && <button onClick={() => {
              trackEvent(profile.analyticsEnabled, "chat_retry");
              void deliver(retry.text, retry.attachment, retry.id, retry.preference, false);
            }}><RotateCcw size={14} /> Try again</button>}</div>}
          </div>
        </div>

        <div className="composer-zone">
          {!user?.isAnonymous && <div className="account-saved"><ShieldCheck size={12} /> Conversation saved to your account</div>}
          <div className="preference-row" aria-label="Response style">
            {([
              ["automatic", "Natural"], ["listen", "Just listen"], ["straight", "Be straight"], ["plan", "Give me a plan"],
            ] as [ResponsePreference, string][]).map(([value, label]) => (
              <button key={value} className={preference === value ? "selected" : ""} onClick={() => setPreference(value)}>{label}</button>
            ))}
          </div>
          {attachment && (
            <div className="attachment-preview">
              <img src={attachment.previewURL} alt="Selected screenshot" />
              <div><b>Screenshot ready</b><span>{attachment.fileName}</span></div>
              <button onClick={() => {
                setAttachment(null);
                trackEvent(profile.analyticsEnabled, "screenshot_action", { action: "removed" });
              }} aria-label="Remove screenshot"><X size={16} /></button>
            </div>
          )}
          <div className="composer">
            <label className="composer-icon" aria-label="Add screenshot">
              {uploadingImage ? <LoaderCircle size={20} className="spin" /> : <ImagePlus size={21} />}
              <input type="file" accept="image/*" onChange={event => handleImage(event.target.files?.[0])} />
            </label>
            <textarea
              ref={inputRef}
              value={draft}
              onChange={event => setDraft(event.target.value.slice(0, 4_000))}
              onKeyDown={handleKeyboard}
              placeholder="Say what’s really going on…"
              rows={1}
            />
            <button className="send-button" onClick={requestSend} disabled={isReplying || (!draft.trim() && !attachment)} aria-label="Send message"><ArrowUp size={22} /></button>
          </div>
          {state.currentAd && <SponsoredSlot ad={state.currentAd} />}
          <p className="composer-disclaimer">Pearlie can get things wrong. Use your judgment for important decisions.</p>
        </div>
      </section>

      {settingsOpen && (
        <SettingsSheet
          state={state}
          user={user}
          onClose={() => setSettingsOpen(false)}
          onOpenAccount={onOpenAccount}
          onChangeTheme={theme => setState(current => ({ ...current, theme, profile: current.profile ? { ...current.profile, selectedTheme: theme, updatedAt: new Date().toISOString() } : null }))}
          onChangeMode={displayMode => setState(current => ({ ...current, displayMode }))}
          onChangeAnalytics={enabled => {
            if (!enabled) trackEvent(false, "session_ended");
            setState(current => ({
              ...current,
              profile: current.profile ? { ...current.profile, analyticsEnabled: enabled, updatedAt: new Date().toISOString() } : null,
            }));
            if (enabled) trackEvent(true, "session_started", { source: "analytics_enabled" });
          }}
          onChangeResearch={enabled => {
            const deletionKey = state.researchDeletionKey;
            setState(current => ({ ...current, researchSharingEnabled: enabled, researchChoiceMade: true }));
            trackEvent(profile.analyticsEnabled, "research_sharing_changed", { enabled });
            if (!enabled) {
              void deleteSharedResearch(deletionKey).then(() => {
                setState(current => ({
                  ...current,
                  researchConversationID: crypto.randomUUID(),
                  researchDeletionKey: crypto.randomUUID(),
                }));
              }).catch(() => undefined);
            }
          }}
          onClear={async () => {
            await Promise.all([
              deleteCloudMemory().catch(() => undefined),
              deleteSharedResearch(state.researchDeletionKey).catch(() => undefined),
            ]);
            onReset();
          }}
        />
      )}

      {savePromptOpen && user?.isAnonymous && (
        <ConversationSavePrompt
          onLater={() => {
            trackEvent(profile.analyticsEnabled, "sign_in_prompt_action", { action: "later" });
            setSavePromptOpen(false);
          }}
          onSignIn={() => {
            trackEvent(profile.analyticsEnabled, "sign_in_prompt_action", { action: "sign_in" });
            setSavePromptOpen(false);
            onOpenAccount();
          }}
        />
      )}

      {researchPromptOpen && (
        <ConversationResearchPrompt
          onShare={() => {
            setState(current => ({
              ...current,
              researchSharingEnabled: true,
              researchChoiceMade: true,
            }));
            trackEvent(profile.analyticsEnabled, "research_prompt_answered", { choice: "share" });
            trackEvent(profile.analyticsEnabled, "research_sharing_changed", { enabled: true });
            setResearchPromptOpen(false);
          }}
          onKeepPrivate={() => {
            setState(current => ({
              ...current,
              researchSharingEnabled: false,
              researchChoiceMade: true,
            }));
            trackEvent(profile.analyticsEnabled, "research_prompt_answered", { choice: "private" });
            trackEvent(profile.analyticsEnabled, "research_sharing_changed", { enabled: false });
            setResearchPromptOpen(false);
          }}
        />
      )}

      {consentPending && (
        <AIConsent
          onCancel={() => { setConsentPending(false); pendingAfterConsent.current = null; }}
          onContinue={() => {
            setState(current => ({ ...current, consentVersion: CONSENT_VERSION }));
            setConsentPending(false);
            const action = pendingAfterConsent.current;
            pendingAfterConsent.current = null;
            action?.();
          }}
        />
      )}
    </div>
  );
}

function ConversationSavePrompt({ onLater, onSignIn }: { onLater: () => void; onSignIn: () => void }) {
  return (
    <div className="sheet-backdrop save-prompt-backdrop" onMouseDown={event => event.target === event.currentTarget && onLater()}>
      <section className="save-prompt">
        <div className="save-prompt-icon"><ShieldCheck size={21} /></div>
        <h2>Don’t lose this conversation.</h2>
        <p>It’s already saved in this browser. Sign in to back it up too, so it comes back after your browser data is cleared or on another device.</p>
        <button className="primary accent" onClick={onSignIn}>Sign in or create an account</button>
        <button className="text-button" onClick={onLater}>Maybe later</button>
      </section>
    </div>
  );
}

function ConversationResearchPrompt({ onShare, onKeepPrivate }: { onShare: () => void; onKeepPrivate: () => void }) {
  return (
    <div className="sheet-backdrop consent-backdrop">
      <section className="consent-sheet research-consent-sheet">
        <div className="consent-icon"><MessageCircleHeart size={23} /></div>
        <span className="eyebrow">YOUR CHOICE</span>
        <h2>Help Pearlie get better?</h2>
        <p>You’ve now had five replies. You can choose to share future text conversations so our team can improve how Pearlie responds.</p>
        <div className="consent-point"><ShieldCheck size={18} /><span><b>De-identified before storage.</b> Common names, emails, phone numbers, links, addresses, and similar identifiers are automatically replaced where possible.</span></div>
        <div className="consent-point"><ShieldCheck size={18} /><span><b>Kept separate from your account.</b> Account details, profile answers, IP addresses, advertising identifiers, screenshots, and safety conversations are excluded.</span></div>
        <div className="research-caution"><b>A privacy note</b><span>Free-form text can still contain personal details after automated redaction. Only share conversations you’re comfortable letting our team review.</span></div>
        <div className="consent-links"><a href="/privacy" target="_blank" rel="noreferrer">Read the Privacy Policy</a></div>
        <button className="primary dark" onClick={onShare}>Share future text chats</button>
        <button className="secondary-consent" onClick={onKeepPrivate}>Keep my chats private<span>No conversation text will be saved for research</span></button>
        <small>You can change this anytime in Settings. Turning sharing off requests deletion of previously shared copies.</small>
      </section>
    </div>
  );
}

/// Blank lines separate paragraphs; single line breaks stay inside one, so a
/// reply reads with the same spacing as the iPhone app.
function paragraphs(content: string): ReactNode[] {
  return content.trim().split(/\n\s*\n/).map((paragraph, index) => (
    <p key={index}>
      {paragraph.split("\n").map((line, lineIndex) => lineIndex === 0 ? line : <span key={lineIndex}><br />{line}</span>)}
    </p>
  ));
}

function Message({ message, isLatest, onFeedback }: { message: ChatMessage; isLatest: boolean; onFeedback: (feedback: "helpful" | "notQuite") => void }) {
  return (
    <article className={`message ${message.role} ${isLatest ? "latest" : ""}`}>
      {message.role === "pearlie" ? (
        <>
          <div className="message-label"><PearlieAvatar size={26} /><span>PEARLIE</span></div>
          <div className="reply">{paragraphs(message.content)}</div>
        </>
      ) : (
        <div className="bubble">
          {message.hadAttachment && <div className="screenshot-chip"><ImagePlus size={13} /> Screenshot attached</div>}
          {paragraphs(message.content)}
        </div>
      )}
      {message.role === "pearlie" && isLatest && (
        <div className="feedback-row">
          <button className={message.feedback === "helpful" ? "selected" : ""} onClick={() => onFeedback("helpful")}><ThumbsUp size={13} /> That helped</button>
          <button className={message.feedback === "notQuite" ? "selected" : ""} onClick={() => onFeedback("notQuite")}><ThumbsDown size={13} /> Not quite</button>
        </div>
      )}
    </article>
  );
}

/// While she's replying her pearl breathes. The only ongoing motion in the chat.
function Presence({ text }: { text: string }) {
  return (
    <div className="presence">
      <PearlieAura diameter={26} sparkles={false} energy={1.8}><PearlieLogo size={26} /></PearlieAura>
      <i className="presence-dot" /><span>{text}</span>
    </div>
  );
}

/// Under Pearlie's first line of a new conversation, until she says something:
/// three first lines from her own onboarding answers. They fill the composer
/// rather than sending, so she can make them her own words.
function Starters({ lines, onPick }: { lines: string[]; onPick: (line: string) => void }) {
  return (
    <div className="starters">
      <div className="starters-label"><i />IF THE WORDS AREN’T THERE YET</div>
      {lines.map((line, index) => {
        const Icon = [Sparkles, Heart, MessageCircleHeart][index % 3];
        return (
          <button key={line} onClick={() => onPick(line)}>
            <span className="starter-icon"><Icon size={13} strokeWidth={2.6} /></span>
            <span>{line}</span>
            <ArrowUpRight size={15} strokeWidth={2.6} />
          </button>
        );
      })}
    </div>
  );
}

/// One sponsored suggestion anchored under the composer, the way ad-supported
/// chat apps earn their keep. Each new ad counts one impression; the same ad
/// staying on screen across turns does not.
function SponsoredSlot({ ad }: { ad: GravityAd }) {
  const tracked = useRef<string | undefined>(undefined);
  const key = ad.impUrl || ad.clickUrl || ad.url;
  useEffect(() => {
    if (!ad.impUrl || tracked.current === key) return;
    tracked.current = key;
    fetch(ad.impUrl, { mode: "no-cors", keepalive: true }).catch(() => undefined);
  }, [ad.impUrl, key]);
  const destination = ad.clickUrl || ad.url;
  return (
    <a className="sponsored-slot" href={destination} target="_blank" rel="noopener noreferrer sponsored">
      {ad.favicon ? <img src={ad.favicon} alt="" /> : <span className="sponsored-slot-mark"><Sparkles size={15} /></span>}
      <span className="sponsored-slot-body">
        <b>{ad.brandName || ad.title || "Sponsored"} <i>AD</i> <ChevronRight size={12} /></b>
        <small>{ad.adText || ad.title}</small>
      </span>
    </a>
  );
}

function SettingsSheet({
  state, user, onClose, onOpenAccount, onChangeTheme, onChangeMode,
  onChangeAnalytics, onChangeResearch, onClear,
}: {
  state: PersistedState;
  user: User | null;
  onClose: () => void;
  onOpenAccount: () => void;
  onChangeTheme: (theme: AppTheme) => void;
  onChangeMode: (mode: DisplayMode) => void;
  onChangeAnalytics: (enabled: boolean) => void;
  onChangeResearch: (enabled: boolean) => void;
  onClear: () => Promise<void>;
}) {
  const [confirmClear, setConfirmClear] = useState(false);
  const [pane, setPane] = useState<"appearance" | "account" | "privacy" | "data">("appearance");
  const paneCopy = {
    appearance: ["Appearance", "Make Pearlie feel right on this screen."],
    account: ["Account", "Keep your private conversation available across devices."],
    privacy: ["Privacy & support", "Control analytics, research sharing, and support resources."],
    data: ["Your data", "Manage the profile, memory, and conversation stored by Pearlie."],
  } as const;
  return (
    <div className="sheet-backdrop settings-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section className="settings-dialog">
        <aside className="settings-navigation-panel">
          <div className="settings-brand"><img src="/app/AppIcon-60.png" alt="" /><div><b>Pearlie</b><span>Settings</span></div></div>
          <nav aria-label="Settings sections">
            <button className={pane === "appearance" ? "selected" : ""} onClick={() => setPane("appearance")}><Palette size={18} /><span>Appearance</span></button>
            {ACCOUNT_SIGN_IN_ENABLED && <button className={pane === "account" ? "selected" : ""} onClick={() => setPane("account")}><CircleUserRound size={18} /><span>Account</span></button>}
            <button className={pane === "privacy" ? "selected" : ""} onClick={() => setPane("privacy")}><ShieldCheck size={18} /><span>Privacy & support</span></button>
            <button className={pane === "data" ? "selected" : ""} onClick={() => setPane("data")}><HardDrive size={18} /><span>Your data</span></button>
          </nav>
          <p>Your choices are saved automatically.</p>
        </aside>

        <div className="settings-detail">
          <header>
            <div><span className="eyebrow">YOUR SPACE</span><h2>{paneCopy[pane][0]}</h2><p>{paneCopy[pane][1]}</p></div>
            <button className="circle-button" onClick={onClose} aria-label="Close settings"><X size={19} /></button>
          </header>
          <div className="settings-scroll">
          {pane === "appearance" && <SettingsSection title="Display">
            <div className="mode-grid">
              {(["light", "dark", "system"] as DisplayMode[]).map(mode => <button key={mode} className={state.displayMode === mode ? "selected" : ""} onClick={() => onChangeMode(mode)}>{mode === "light" ? <Sun size={17} /> : mode === "dark" ? <Moon size={17} /> : <Eye size={17} />}<span>{mode === "system" ? "Auto" : capitalize(mode)}</span></button>)}
            </div>
            <h4 className="settings-subtitle">Accent color</h4>
            <div className="theme-grid">
              {(["rose", "peach", "lilac", "sage"] as AppTheme[]).map(theme => <button key={theme} className={state.theme === theme ? "selected" : ""} onClick={() => onChangeTheme(theme)}><i className={`theme-dot ${theme}`} />{capitalize(theme)}{state.theme === theme && <Check size={14} />}</button>)}
            </div>
          </SettingsSection>}

          {ACCOUNT_SIGN_IN_ENABLED && pane === "account" && (
            <SettingsSection title="Conversation backup">
              <div className="settings-explainer"><Cloud size={20} /><div><b>{user?.isAnonymous ? "Your conversation is saved locally" : "Your private backup is active"}</b><p>{user?.isAnonymous ? "It will return in this browser. Connect an account to restore it after clearing browser data or when using another device." : "Your profile, memory, and recent conversation can be restored when you use the same account on another device."}</p></div></div>
              <button className="settings-row" onClick={onOpenAccount}><CircleUserRound size={19} /><span><b>{user?.isAnonymous ? "Save your conversations" : user?.email || "Signed in"}</b><small>{user?.isAnonymous ? "Continue on another device" : "Your private backup is active"}</small></span><ChevronRight size={17} /></button>
            </SettingsSection>
          )}

          {pane === "privacy" && <SettingsSection title="Controls">
            <label className="settings-row compact"><Sparkles size={18} /><span><b>App analytics</b><small>Usage stats sent only to Pearlie’s own server. Never your messages or answers.</small></span><input type="checkbox" checked={state.profile?.analyticsEnabled ?? true} onChange={event => onChangeAnalytics(event.target.checked)} /></label>
            <label className="settings-row compact"><MessageCircleHeart size={18} /><span><b>Share chats to improve Pearlie</b><small>Let our team review de-identified text chats; no account details or screenshots</small></span><input type="checkbox" checked={state.researchSharingEnabled} onChange={event => onChangeResearch(event.target.checked)} /></label>
            <a className="settings-row compact" href="/privacy" target="_blank"><ShieldCheck size={18} /><span><b>Privacy policy</b></span><ChevronRight size={17} /></a>
            <a className="settings-row compact" href="/terms" target="_blank"><MessageCircleHeart size={18} /><span><b>Terms of service</b></span><ChevronRight size={17} /></a>
            <a className="settings-row compact" href="/support" target="_blank"><LogIn size={18} /><span><b>Support</b></span><ChevronRight size={17} /></a>
          </SettingsSection>}

          {pane === "data" && <SettingsSection title="Memory and conversation">
            <div className="settings-explainer"><HardDrive size={20} /><div><b>Pearlie remembers this conversation</b><p>Your profile, saved memory, and chat remain available in this browser. Deleting them here cannot be undone.</p></div></div>
            {!confirmClear ? (
              <button className="settings-row danger" onClick={() => setConfirmClear(true)}><Trash2 size={18} /><span><b>Delete Pearlie’s memory</b><small>Profile, chat and private backup</small></span></button>
            ) : (
              <div className="danger-confirm"><p>This permanently removes your conversation and saved memory.</p><div><button onClick={() => setConfirmClear(false)}>Cancel</button><button onClick={onClear}>Delete everything</button></div></div>
            )}
          </SettingsSection>}
          </div>
        </div>
      </section>
    </div>
  );
}

function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return <section className="settings-section"><h3>{title}</h3>{children}</section>;
}

function AuthenticationSheet({ user, onClose, onSignedIn }: { user: User | null; onClose: () => void; onSignedIn: (user: User) => void }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const run = async (action: () => Promise<{ user: User }>) => {
    setBusy(true); setError("");
    try { onSignedIn((await action()).user); }
    catch (authError) { setError(friendlyAuthError(authError)); }
    finally { setBusy(false); }
  };

  const emailSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (password.length < 6) { setError("Use at least six characters for your password."); return; }
    run(() => signInOrCreateWithEmail(email.trim(), password));
  };

  return (
    <div className="sheet-backdrop auth-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section className="auth-sheet">
        <button className="auth-close" onClick={onClose}><X size={18} /></button>
        <img src="/app/AppIcon-120.png" alt="Pearlie" />
        <span className="eyebrow">KEEP YOUR CONVERSATIONS</span>
        <h2>{user?.isAnonymous ? "Take Pearlie with you." : "Your conversations are saved."}</h2>
        <p>Sign in to restore your private chat and memory on another device. You can still use Pearlie without an account.</p>

        {!emailOpen ? (
          <div className="auth-options">
            <button disabled={busy} className="apple-auth" onClick={() => run(signInWithApple)}><span className="apple-glyph"></span> Continue with Apple</button>
            <button disabled={busy} onClick={() => run(signInWithGoogle)}><span className="google-glyph">G</span> Continue with Google</button>
            <button disabled={busy} onClick={() => setEmailOpen(true)}><span className="email-glyph">@</span> Continue with email</button>
          </div>
        ) : (
          <form className="email-auth" onSubmit={emailSubmit}>
            <input type="email" placeholder="Email address" value={email} onChange={event => setEmail(event.target.value)} required autoComplete="email" />
            <label><input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={event => setPassword(event.target.value)} required autoComplete="current-password" /><button type="button" onClick={() => setShowPassword(value => !value)}>{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button></label>
            <button className="primary dark" disabled={busy}>{busy ? <LoaderCircle className="spin" size={18} /> : "Continue"}</button>
            <button type="button" className="text-button" onClick={() => setEmailOpen(false)}>Use another method</button>
          </form>
        )}
        {error && <div className="auth-error">{error}</div>}
        <button className="continue-private" onClick={onClose}>Continue privately</button>
        <small>By continuing, you agree to Pearlie’s <a href="/terms" target="_blank">Terms</a> and acknowledge the <a href="/privacy" target="_blank">Privacy Policy</a>.</small>
      </section>
    </div>
  );
}

function AIConsent({ onCancel, onContinue }: { onCancel: () => void; onContinue: () => void }) {
  return (
    <div className="sheet-backdrop consent-backdrop">
      <section className="consent-sheet ai-consent-sheet">
        <div className="consent-icon"><ShieldCheck size={22} /></div>
        <h2>Before Pearlie replies</h2>
        <p>Choose whether Pearlie can securely use what is needed to answer you.</p>
        <div className="consent-point"><MessageSquareText size={18} /><span><b>To write your reply</b>Pearlie securely sends your message, recent chat context, relevant profile answers, and any screenshot you choose to attach to secure AI servers so your reply can be written.</span></div>
        <div className="consent-point"><Sparkles size={18} /><span><b>For sponsored suggestions</b>Pearlie may show clearly labeled sponsored suggestions in the future. Chat context is not currently shared for sponsor matching.</span></div>
        <div className="consent-note"><ShieldCheck size={16} /><span>If sponsored matching is enabled later, Pearlie will explain what is shared and ask for permission first.</span></div>
        <div className="consent-links"><a href="/privacy" target="_blank">Read the privacy policy</a></div>
        <button className="primary" onClick={onContinue}>Agree &amp; Continue <ArrowRight size={16} /></button>
        <button className="text-button" onClick={onCancel}>Not now</button>
      </section>
    </div>
  );
}

function capitalize(value: string) { return value.charAt(0).toUpperCase() + value.slice(1); }

function friendlyAuthError(error: unknown): string {
  const code = typeof error === "object" && error && "code" in error ? String((error as { code: unknown }).code) : "";
  if (code.includes("popup-closed")) return "Sign-in was closed before it finished.";
  if (code.includes("popup-blocked")) return "Allow the sign-in window, then try again.";
  if (code.includes("unauthorized-domain")) return "Sign-in is temporarily unavailable on this address.";
  if (code.includes("operation-not-allowed")) return "That sign-in option is temporarily unavailable. Try Google or email instead.";
  if (code.includes("wrong-password") || code.includes("invalid-credential")) return "That email or password doesn’t match.";
  if (code.includes("email-already")) return "That email already has an account. Sign in with the same method you used before.";
  if (code.includes("network")) return "Check your connection and try again.";
  return "Pearlie couldn’t complete sign-in. Try again.";
}

export default App;

// Keep these account helpers reachable for the settings expansion without
// shipping a second auth implementation later.
void leaveAccountForPrivateSession;
void deleteFirebaseAccount;
